<template>
  <div v-if="visible" class="public-questionnaire" :class="{ decorated }">
    <div v-if="loading" class="questionnaire-loading">
      <van-loading size="22px" type="spinner" color="#b92504" />
      <span>问卷加载中...</span>
    </div>

    <template v-else-if="questions.length">
      <h2 v-if="showTitle" class="title">{{ surveyTitle }}</h2>
      <div v-if="description" class="description">{{ description }}</div>
      <div v-if="physicianName" class="sub-title">医生：{{ physicianName | displayPhysicianName }}</div>

      <div class="questions">
        <div v-for="item in visibleItems" :key="item.idx" class="question">
          <p class="q-text">
            <span class="q-index">{{ item.idx + 1 }}.</span>
            <span class="q-title">{{ item.q.text }}</span>
            <span v-if="isRequiredQuestion(item.q)" class="req">*</span>
          </p>

          <van-field
            v-if="isTextQuestion(item.q) && answers[item.idx]"
            v-model="answers[item.idx].value"
            type="textarea"
            :rows="item.q.rows || 2"
            :autosize="item.q.autosize !== false"
            :maxlength="item.q.maxlength"
            :show-word-limit="item.q.maxlength !== undefined && item.q.maxlength !== null"
            :placeholder="item.q.placeholder || '请输入'"
            class="q-textarea"
          />

          <van-checkbox-group
            v-else-if="isMultipleQuestion(item.q) && answers[item.idx]"
            v-model="answers[item.idx].value"
            class="answer"
          >
            <van-checkbox v-for="opt in getQuestionOptions(item.q)" :key="opt.value" :name="opt.value" shape="square">
              {{ getOptionText(opt) }}
            </van-checkbox>
          </van-checkbox-group>

          <van-radio-group
            v-else-if="isRadioQuestion(item.q) && answers[item.idx]"
            v-model="answers[item.idx].value"
            :direction="item.q.direction || 'vertical'"
            class="answer"
          >
            <van-radio v-for="opt in getQuestionOptions(item.q)" :key="opt.value" :name="opt.value">
              {{ getOptionText(opt) }}
            </van-radio>
          </van-radio-group>

          <p v-if="showTips && isMissing(item.idx)" class="error">这是必答题，请填写/选择答案！</p>
        </div>
      </div>

      <div v-if="statusText" class="submit-status" :class="submitStatus">
        {{ statusText }}
      </div>

      <div v-if="effectiveShowSubmitButton" class="actions">
        <van-button type="primary" color="#b92504" block :loading="submitting" @click="onSubmit()">
          {{ submitButtonText }}
        </van-button>
      </div>
    </template>
  </div>
</template>

<script>
import { Button, RadioGroup, Radio, Loading, Field, Checkbox, CheckboxGroup, Toast } from 'vant';
import { getQuestionnaire, getQuestionnaireById, saveQuestionnaireResult } from '@/api/content';

export default {
  name: 'PublicQuestionnaire',
  components: {
    'van-button': Button,
    'van-radio-group': RadioGroup,
    'van-radio': Radio,
    'van-loading': Loading,
    'van-field': Field,
    'van-checkbox': Checkbox,
    'van-checkbox-group': CheckboxGroup,
  },
  props: {
    contentId: { type: String, default: '' },
    questionnaireId: { type: String, default: '' },
    openId: { type: String, default: '' },
    physician: { type: Object, default: null },
    showTitle: { type: Boolean, default: true },
    showSubmitButton: { type: Boolean, default: true },
    submitButtonText: { type: String, default: '提交' },
    decorated: { type: Boolean, default: false },
  },
  data() {
    return {
      visible: true,
      loading: true,
      surveyTitle: '问卷',
      description: '',
      qid: '',
      questions: [],
      answers: [],
      showTips: false,
      submitting: false,
      submitStatus: '',
      submitted: false,
    };
  },
  computed: {
    physicianName() {
      return this.physician && this.physician.physician_name ? this.physician.physician_name : '';
    },
    visibleItems() {
      return (this.questions || [])
        .map((q, idx) => ({ q, idx }))
        .filter(({ q, idx }) => this.isQuestionVisible(q, idx));
    },
    hasManualOnlyQuestion() {
      return this.visibleItems.some(({ q }) => this.isTextQuestion(q) || this.isMultipleQuestion(q));
    },
    effectiveShowSubmitButton() {
      return this.showSubmitButton || this.hasManualOnlyQuestion;
    },
    statusText() {
      if (this.submitStatus === 'success') return '提交成功，感谢您的参与！';
      if (this.submitStatus === 'failed') return '提交失败，请稍后重试。';
      return '';
    },
  },
  watch: {
    contentId() {
      this.bootstrap();
    },
    questionnaireId() {
      this.bootstrap();
    },
    answers: {
      deep: true,
      handler() {
        this.clearHiddenAnswers();
      },
    },
  },
  mounted() {
    this.bootstrap();
  },
  methods: {
    async bootstrap() {
      if (!this.contentId && !this.questionnaireId) {
        this.visible = false;
        this.loading = false;
        this.$emit('empty');
        return;
      }

      this.visible = true;
      this.loading = true;
      this.showTips = false;
      this.submitStatus = '';
      this.submitted = false;
      this.description = '';
      this.questions = [];
      this.answers = [];

      try {
        const res = this.questionnaireId
          ? await getQuestionnaireById(this.questionnaireId)
          : await getQuestionnaire(this.contentId);
        const envelope = res && res.data !== undefined ? res.data : res;
        const q = envelope && envelope.data ? envelope.data : envelope;
        const parsedConfig = q ? this.tryParseQuestionnaireConfig(q.questions) : null;
        const parsed = parsedConfig && parsedConfig.questions;

        if (!q || !Array.isArray(parsed) || !parsed.length) {
          this.visible = false;
          this.$emit('empty');
          return;
        }

        this.qid = q.questionnaire_id || this.questionnaireId || this.contentId;
        this.surveyTitle = q.questionnaire_name || q.title || '问卷';
        this.description = q.description || parsedConfig.description || '';
        this.questions = parsed;
        this.initAnswers();
        this.$emit('loaded', {
          questionnaireId: this.qid,
          title: this.surveyTitle,
          description: this.description,
          questions: this.questions,
        });
      } catch (e) {
        this.visible = false;
        this.$emit('error', e);
      } finally {
        this.loading = false;
      }
    },
    initAnswers() {
      this.answers = (this.questions || []).map((q, idx) => ({
        index: idx + 1,
        question: q.text,
        value: this.getInitialAnswerValue(q),
        label: '',
      }));
    },
    getQuestionType(q) {
      const t = String((q && (q.type || q.input || q.kind)) || '').toLowerCase();
      if (['checkbox', 'checkboxes', 'multiple', 'multiselect', 'multi'].includes(t) || (q && q.multiple === true)) {
        return 'checkbox';
      }
      if (['textarea', 'text', 'input', 'free', 'freetext'].includes(t) || (q && q.widget === 'textarea')) {
        return 'textarea';
      }
      return 'radio';
    },
    getInitialAnswerValue(q) {
      return this.getQuestionType(q) === 'checkbox' ? [] : '';
    },
    isTextQuestion(q) {
      return this.getQuestionType(q) === 'textarea';
    },
    isMultipleQuestion(q) {
      return this.getQuestionType(q) === 'checkbox';
    },
    isRadioQuestion(q) {
      return this.getQuestionType(q) === 'radio';
    },
    isRequiredQuestion(q) {
      return !q || q.required !== false;
    },
    getQuestionOptions(q) {
      return q && Array.isArray(q.options) ? q.options : [];
    },
    getOptionText(opt) {
      if (!opt) return '';
      return `${opt.labelPrefix || ''}${opt.label || ''}`;
    },
    isQuestionVisible(q) {
      const cond = q && (q.visibleWhen || q.showIf);
      if (!cond) return true;

      const evalOne = (c) => {
        if (!c) return true;
        const ref = c.questionIndex !== undefined
          ? c.questionIndex
          : (c.question !== undefined ? c.question : (c.index !== undefined ? c.index : (c.q !== undefined ? c.q : c.ref)));
        let refIdx = -1;
        if (c.questionIndex !== undefined) {
          refIdx = Number(ref);
        } else if (typeof ref === 'number') {
          refIdx = ref - 1;
        } else if (typeof ref === 'string') {
          const m = ref.match(/^q(\d+)$/i);
          refIdx = m ? parseInt(m[1], 10) - 1 : parseInt(ref, 10) - 1;
        }
        if (!(refIdx >= 0 && refIdx < this.answers.length)) return false;

        const val = this.answers[refIdx] && this.answers[refIdx].value;
        const values = Array.isArray(val) ? val : [val];
        const refQ = this.questions[refIdx] || {};
        const labels = this.getQuestionOptions(refQ)
          .filter(o => values.includes(o.value))
          .map(o => o.label);

        if ('value' in c) {
          const expected = Array.isArray(c.value) ? c.value : [c.value];
          return expected.some(i => values.includes(i));
        }
        if ('values' in c) {
          const expected = Array.isArray(c.values) ? c.values : [c.values];
          return expected.some(i => values.includes(i));
        }
        if ('equals' in c) {
          const expected = Array.isArray(c.equals) ? c.equals : [c.equals];
          return expected.some(i => values.includes(i));
        }
        if ('in' in c) return Array.isArray(c.in) ? c.in.some(i => values.includes(i)) : false;
        if ('labelEquals' in c) {
          const expected = Array.isArray(c.labelEquals) ? c.labelEquals : [c.labelEquals];
          return expected.some(i => labels.includes(i));
        }
        if ('labelIn' in c) return Array.isArray(c.labelIn) ? c.labelIn.some(i => labels.includes(i)) : false;

        return Array.isArray(val) ? val.length > 0 : !!val;
      };

      if (Array.isArray(cond)) return cond.every(evalOne);
      if (cond.allOf && Array.isArray(cond.allOf)) return cond.allOf.every(evalOne);
      if (cond.anyOf && Array.isArray(cond.anyOf)) return cond.anyOf.some(evalOne);
      return evalOne(cond);
    },
    clearHiddenAnswers() {
      (this.questions || []).forEach((q, idx) => {
        if (this.isQuestionVisible(q, idx) || !this.answers[idx]) return;

        const emptyValue = this.getInitialAnswerValue(q);
        if (this.isSameAnswerValue(this.answers[idx].value, emptyValue)) return;

        this.$set(this.answers, idx, {
          ...this.answers[idx],
          value: emptyValue,
          label: '',
        });
      });
    },
    isSameAnswerValue(a, b) {
      if (Array.isArray(a) || Array.isArray(b)) {
        return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((item, index) => item === b[index]);
      }
      return a === b;
    },
    isMissing(idx) {
      const q = this.questions[idx] || {};
      if (!this.isQuestionVisible(q, idx)) return false;
      if (!this.isRequiredQuestion(q)) return false;
      const v = this.answers[idx] ? this.answers[idx].value : '';
      if (this.isMultipleQuestion(q)) return !Array.isArray(v) || v.length === 0;
      return this.isTextQuestion(q) ? String(v || '').trim() === '' : !v;
    },
    buildCompletedAnswers() {
      const visibleSet = new Set(this.visibleItems.map(i => i.idx));
      return this.answers
        .map((ans, idx) => {
          if (!visibleSet.has(idx)) return null;
          const q = this.questions[idx] || {};

          if (this.isTextQuestion(q)) {
            return { ...ans, label: String(ans.value || '') };
          }

          if (this.isMultipleQuestion(q)) {
            const values = Array.isArray(ans.value) ? ans.value : [];
            const labels = this.getQuestionOptions(q)
              .filter(o => values.includes(o.value))
              .map(o => o.label);
            return { ...ans, label: labels.join(',') };
          }

          const opt = this.getQuestionOptions(q).find(o => o.value === ans.value);
          return { ...ans, label: opt ? opt.label : '' };
        })
        .filter(Boolean);
    },
    buildPayload(completed) {
      const physician = this.physician || {};
      return {
        questionnaire_id: this.qid || this.questionnaireId || this.contentId,
        account_id: String(physician.account_id || ''),
        open_id: String(physician.open_id || this.openId || ''),
        physician_id: String(physician.physician_id || ''),
        answers: JSON.stringify(completed),
        recorder_type: 'physician',
        recorder_id: String(physician.physician_id || ''),
        recorder_name: String(physician.physician_name || ''),
      };
    },
    canSubmit() {
      return this.visibleItems.every(({ idx }) => !this.isMissing(idx));
    },
    async onSubmit() {
      if (this.submitting || this.submitted) return;
      if (!this.canSubmit()) {
        this.showTips = true;
        return;
      }

      const completed = this.buildCompletedAnswers();
      const payload = this.buildPayload(completed);

      try {
        this.submitting = true;
        this.submitStatus = '';
        await saveQuestionnaireResult(payload);
        this.submitted = true;
        this.submitStatus = 'success';
        Toast.success('提交成功');
        this.$emit('submitted', {
          questionnaireId: this.qid,
          answers: completed,
          payload,
        });
      } catch (e) {
        this.submitStatus = 'failed';
        Toast.fail('提交失败，请稍后重试');
        this.$emit('submit-error', e);
      } finally {
        this.submitting = false;
      }
    },
    tryParseQuestionnaireConfig(raw) {
      if (!raw) return null;
      if (Array.isArray(raw)) {
        return { questions: raw };
      }
      if (typeof raw === 'object') {
        if (Array.isArray(raw.questions)) {
          return {
            description: raw.description || '',
            questions: raw.questions,
          };
        }
        return null;
      }
      if (typeof raw === 'string') {
        try {
          const j = JSON.parse(raw);
          if (Array.isArray(j)) return { questions: j };
          if (j && Array.isArray(j.questions)) {
            return {
              description: j.description || '',
              questions: j.questions,
            };
          }
        } catch (e) {
          // fallback below
        }
        try {
          // eslint-disable-next-line no-new-func
          const v = new Function(`return (${raw})`)();
          if (Array.isArray(v)) return { questions: v };
          if (v && Array.isArray(v.questions)) {
            return {
              description: v.description || '',
              questions: v.questions,
            };
          }
        } catch (e) {
          // ignore invalid questionnaire shape
        }
      }
      return null;
    },
  },
};
</script>

<style lang="scss" scoped>
.public-questionnaire {
  padding: 24px 16px 64px 16px;
  text-align: left;
  background: linear-gradient(180deg, #f6f4f1 0%, #fbfaf8 100%);
  border-radius: 8px;
  color: #242424;

  &.decorated {
    background-image: url(https://lundbeck-superrep-public-resource-prod.s3.cn-northwest-1.amazonaws.com.cn/app/pim/image/home/home-header-bg.png);
    background-repeat: no-repeat;
    background-position: top center;
    background-size: 100% auto;
  }

  .questionnaire-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 120px;
    color: #666;
    font-size: 13px;
  }

  .title {
    font-size: 16px;
    font-weight: 700;
    margin: 8px 0 18px;
    color: #2a2a2a;
  }

  .sub-title {
    font-size: 13px;
    color: #666;
    margin-bottom: 12px;
  }

  .description {
    font-size: 13px;
    line-height: 1.7;
    font-weight: 600;
    color: #666;
    margin: 0 0 22px;
    padding-bottom: 18px;
    border-bottom: 1px solid #e8e2da;
    white-space: pre-wrap;
  }

  .question {
    margin-bottom: 22px;
    padding-bottom: 6px;

    & + .question {
      border-top: 1px solid #e8e2da;
      padding-top: 22px;
    }
  }

  .q-text {
    font-size: 13px;
    font-weight: 700;
    line-height: 1.7;
    margin: 0 0 18px;
    color: #222;
    display: flex;
    align-items: flex-start;

    .q-index {
      color: #bf2f18;
      font-size: 15px;
      line-height: 1.5;
      margin-right: 8px;
      flex: 0 0 auto;
    }

    .q-title {
      flex: 1;
    }

    .req {
      color: #cc4b47;
      margin-left: 4px;
    }
  }

  ::v-deep .van-radio,
  ::v-deep .van-checkbox {
    font-size: 13px;
    align-items: center;
    min-height: 38px;
    margin-bottom: 8px;
    padding: 7px 12px;
    background: rgba(255, 255, 255, 0.88);
    border: 1px solid #e8e2da;
    border-radius: 5px;
    box-shadow: 0 1px 2px rgba(80, 55, 35, 0.04);
    transition: border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;
  }

  ::v-deep .van-radio__label,
  ::v-deep .van-checkbox__label {
    color: #666;
    line-height: 1.35;
    margin-left: 10px;
  }

  ::v-deep .van-radio__icon,
  ::v-deep .van-checkbox__icon {
    font-size: 18px;
    flex: 0 0 auto;
  }

  ::v-deep .van-radio__icon--checked .van-icon,
  ::v-deep .van-checkbox__icon--checked .van-icon {
    background-color: #b92504;
    border-color: #b92504;
  }

  .q-textarea {
    border: 1px solid #e8e2da;
    border-radius: 5px;
    padding: 8px 12px;
    background: rgba(255, 255, 255, 0.92);
    box-sizing: border-box;
  }

  ::v-deep .q-textarea .van-field__control {
    min-height: 64px;
    line-height: 1.5;
  }

  .error {
    font-size: 11px;
    color: #b92504;
    margin-top: 4px;
  }

  .submit-status {
    font-size: 12px;
    line-height: 1.5;
    margin-top: 16px;
    color: #666;

    &.success {
      color: #237b3a;
    }

    &.failed {
      color: #b92504;
    }
  }

  .actions {
    margin-top: 24px;
  }
}
</style>
